// Text + status normalisation, ported verbatim from the original app
// (rule-engine reference §2 / §6). Applied to BOTH finding text and concept
// terms before matching, so the synonym folding must stay byte-identical.

export const COMPLIANCE_SYNONYMS: ReadonlyArray<readonly [string, string]> = [
  ['not on file', 'missing'],
  ['not attached', 'missing'],
  ['not uploaded', 'missing'],
  ['not provided', 'missing'],
  ['not submitted', 'missing'],
  ['not available', 'missing'],
  ['absent', 'missing'],
  ['unsigned', 'not signed'],
  ['no signature', 'not signed'],
  ['unapproved', 'not approved'],
  ['approval missing', 'not approved'],
  ['did not approve', 'not approved'],
  ['not tax compliant', 'tax non compliant'],
  ['tax pin expired', 'tax expired'],
  ['outside the validity period', 'award outside validity'],
  ['validity period', 'validity'],
  ['declaration of interest', 'doi'],
  ['conflict of interest', 'coi'],
  ['electronic document management system', 'edms'],
  ['open text', 'opentext'],
  ['bbbee', 'bbbbee'],
  ['b-bbee', 'bbbbee'],
  ['bbee', 'bbbbee'],
];

/** Lowercase → ordered synonym fold → collapse non-alphanumerics → trim. */
export function normalizeFindingText(text: string): string {
  let t = String(text ?? '').toLowerCase();
  for (const [from, to] of COMPLIANCE_SYNONYMS) {
    t = t.split(from).join(to);
  }
  return t
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const CLEARED_STATUSES = ['Resolved', 'Compliant', 'Resolved , Compliant', 'Resolved .....'];

export function isClearedStatus(status: string): boolean {
  return CLEARED_STATUSES.includes(status);
}

/** Canonicalise a raw "gaps status" cell into the controlled status vocabulary. */
export function normalizeStatusValue(value: string | null | undefined): string {
  const rawTrimmed = String(value ?? '').trim();
  if (!rawTrimmed) return '';
  const v = rawTrimmed.toLowerCase();
  if (/self[-\s]*declar/.test(v) || /closed via self declaration/.test(v)) return 'Self-Declared';
  if (v === 'unresolved' || v === 'unresloved') return 'Unresolved';
  if (v === 'partially resolved') return 'Partially Resolved';
  if (v === 'to delete') return 'To delete';
  if (v === 'deleted') return 'Deleted';
  if (v === 'resolved , compliant' || v === 'resolved, compliant') return 'Resolved , Compliant';
  if (/^resolved\s*\.{2,}$/.test(v)) return 'Resolved .....';
  if (v === 'resolved') return 'Resolved';
  if (v === 'compliant') return 'Compliant';
  return rawTrimmed;
}

// --- Four-state gap/finding lifecycle (alignment call, 9 Jun 2026) -----------
// Gap + finding statuses are standardised to: Open, In Progress, Closed,
// Closed by Self-Declaration. The legacy register vocabulary (Unresolved /
// Partially Resolved / Resolved / Self-declared) and raw import spellings map
// onto it at READ/DISPLAY time — additive, no data migration. An unreviewed
// transaction carries NO gap status ('').

/**
 * Canonicalise any gap-status value — legacy register vocabulary, raw import
 * cells, or the new four-state values — onto the four-state lifecycle.
 * 'Pending Review' / blank → '' (no gaps until reviewed). Unknown values pass
 * through unchanged (never invent a state).
 */
export function normalizeGapStatus(value: string | null | undefined): string {
  const canonical = normalizeStatusValue(value);
  if (!canonical) return '';
  const v = canonical.toLowerCase();
  if (v === 'open' || v === 'unresolved') return 'Open';
  if (v === 'in progress' || v === 'partially resolved') return 'In Progress';
  if (v.startsWith('closed by self') || v === 'self-declared') return 'Closed by Self-Declaration';
  if (
    v === 'closed' ||
    v === 'resolved' ||
    v === 'compliant' ||
    v === 'resolved , compliant' ||
    /^resolved\s*\.{2,}$/.test(v)
  ) {
    return 'Closed';
  }
  if (v === 'pending review') return '';
  return canonical;
}

/** The four lifecycle states (matching enums.GAP_STATUS_OPTIONS, dependency-free here). */
const FOUR_STATE = new Set(['Open', 'In Progress', 'Closed', 'Closed by Self-Declaration']);

/**
 * Canonicalise a FINDING's lifecycle status (effective ?? legacy) onto the same
 * four states. Differs from {@link normalizeGapStatus} in its default: any
 * other NON-BLANK capture (a CES result like "Non-Compliant - PFMA", "Pending
 * Review", "Additional comments") means a finding was recorded and not yet
 * closed — i.e. **Open** — so the register's Status column always reads as a
 * lifecycle state.
 */
export function normalizeFindingStatus(value: string | null | undefined): string {
  if (String(value ?? '').trim() === '') return '';
  const four = normalizeGapStatus(value);
  return FOUR_STATE.has(four) ? four : 'Open';
}

/** First non-blank string among the arguments (after trimming). */
export function firstNonBlank(...vals: Array<string | null | undefined>): string {
  for (const v of vals) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
}
